import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { CellValueChangedEvent, UndoEndedEvent, RedoEndedEvent } from 'ag-grid-enterprise'
import _ from 'lodash'
import { AccountMapRule } from '../../types/AccountMapRule'
import { useCtxAccountMapRules } from '../../queries/useCtxAccountMapRules'
import {
  AccountMapGridRow,
  transformGridRowsToRules,
  createEmptyGridRow,
  getNextRowIndexForStatementRow,
  transformServerDataToGridRows
} from '../../utils/accountMapUtils'
import { StatementRow } from '../../../../types/statementRow/StatementRow'
import { isIncompletePair, isFromGreaterThanTo } from './useAccountMapColumns'

interface UseAccountMapGridDataParams {
  companyIds: string[]
  statementRows?: StatementRow[]
  editable: boolean
  onChange?: (rules: AccountMapRule[]) => void
}

export function useAccountMapGridData({ companyIds, statementRows, editable, onChange }: UseAccountMapGridDataParams) {
  const gridRef = useRef<AgGridReact<AccountMapGridRow>>(null)
  const { data: serverData, isLoading } = useCtxAccountMapRules()

  const [rowData, setRowData] = useState<AccountMapGridRow[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [validationErrors, setValidationErrors] = useState(false)
  const [resetKey, setResetKey] = useState(0)

  // resetKey forces fresh transformation on discard (creates new object references)
  const transformedServerData = useMemo(
    () => transformServerDataToGridRows(serverData, statementRows, companyIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverData, statementRows, companyIds, resetKey]
  )

  // Sync server data to local state when it changes (resetKey forces re-sync on discard)
  useEffect(() => {
    if (!hasUnsavedChanges) {
      setRowData(_.cloneDeep(transformedServerData))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedServerData, hasUnsavedChanges, resetKey])

  // Redraw rows when editable changes to update plus button visibility
  useEffect(() => {
    gridRef.current?.api?.redrawRows()
  }, [editable])

  const handleDiscard = useCallback(() => {
    setHasUnsavedChanges(false)
    setValidationErrors(false)
    setResetKey(k => k + 1) // Force grid remount and fresh data clone
  }, [])

  const isUndoRedoActive = useRef(false)

  const getLiveRows = useCallback((): AccountMapGridRow[] => {
    const rows: AccountMapGridRow[] = []
    gridRef.current?.api?.forEachNode(node => {
      if (node.data) rows.push(node.data)
    })
    return rows
  }, [])

  const handleAddRowForStatementRow = useCallback(
    (statementRowId: number) => {
      const currentRows = getLiveRows()
      const newRowIndex = getNextRowIndexForStatementRow(currentRows, statementRowId)
      const newRow = createEmptyGridRow(statementRowId, newRowIndex, companyIds)

      gridRef.current?.api?.applyTransaction({ add: [newRow] })
      setHasUnsavedChanges(true)
      onChange?.(transformGridRowsToRules([...currentRows, newRow]))

      setTimeout(() => {
        const rowNode = gridRef.current?.api?.getRowNode(newRow.rowId)
        if (rowNode?.displayed) {
          gridRef.current?.api.flashCells({ rowNodes: [rowNode] })
        }
      }, 100)
    },
    [companyIds, onChange, getLiveRows]
  )

  // Don't update rowData state on cell edits — let AG Grid manage its own data.
  // Updating rowData prop during edits resets the undo/redo stack.
  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<AccountMapGridRow>): void => {
      if (!event.data) return
      setHasUnsavedChanges(true)
      // Skip onChange during undo/redo — onUndoEnded/onRedoEnded will call it once after
      if (!isUndoRedoActive.current && onChange) {
        onChange(transformGridRowsToRules(getLiveRows()))
      }
    },
    [onChange, getLiveRows]
  )

  const onUndoStarted = useCallback(() => {
    isUndoRedoActive.current = true
  }, [])

  const onUndoEnded = useCallback(
    (event: UndoEndedEvent) => {
      isUndoRedoActive.current = false
      if (event.operationPerformed && onChange) {
        onChange(transformGridRowsToRules(getLiveRows()))
      }
    },
    [onChange, getLiveRows]
  )

  const onRedoStarted = useCallback(() => {
    isUndoRedoActive.current = true
  }, [])

  const onRedoEnded = useCallback(
    (event: RedoEndedEvent) => {
      isUndoRedoActive.current = false
      if (event.operationPerformed && onChange) {
        onChange(transformGridRowsToRules(getLiveRows()))
      }
    },
    [onChange, getLiveRows]
  )

  const getRefMethods = useCallback(
    () => ({
      getRowData: () => {
        gridRef.current?.api?.stopEditing()
        const liveRows: AccountMapGridRow[] = []
        gridRef.current?.api?.forEachNode(node => {
          if (node.data) liveRows.push(node.data)
        })
        return liveRows
      },
      getRules: () => {
        gridRef.current?.api?.stopEditing()
        const liveRows: AccountMapGridRow[] = []
        gridRef.current?.api?.forEachNode(node => {
          if (node.data) liveRows.push(node.data)
        })
        return transformGridRowsToRules(liveRows)
      },
      hasChanges: () => hasUnsavedChanges,
      validate: () => {
        gridRef.current?.api?.stopEditing()
        const liveRows: AccountMapGridRow[] = []
        gridRef.current?.api?.forEachNode(node => {
          if (node.data) liveRows.push(node.data)
        })
        const isValid = !liveRows.some(row =>
          companyIds.some(cId => isIncompletePair(row, cId) || isFromGreaterThanTo(row, cId))
        )
        setValidationErrors(!isValid)
        if (!isValid) {
          setTimeout(() => gridRef.current?.api?.refreshCells({ force: true }), 0)
        }
        return isValid
      },
      discardChanges: handleDiscard,
      resetToServerData: handleDiscard
    }),
    [hasUnsavedChanges, companyIds, handleDiscard]
  )

  return {
    gridRef,
    rowData,
    resetKey,
    validationErrors,
    isLoading,
    handleAddRowForStatementRow,
    onCellValueChanged,
    onUndoStarted,
    onUndoEnded,
    onRedoStarted,
    onRedoEnded,
    getRefMethods
  }
}
